/**
 * artShared.jsx — Primitives shared across Art Department tabs.
 *
 * Extracted from ArtByScene.jsx during Phase 5 (2026-02) so both
 * ArtByScene and ArtByLocation can reuse the same tinted-panel look,
 * chip semantics, badges, and lightbox without duplication.
 *
 * NOTHING here mutates state that isn't passed in via props/callbacks —
 * the mutation logic still lives inside each tab's Drawer instance.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Plus, Camera, Link2, MapPin as PinIcon, MoreHorizontal, ArrowUp, ArrowDown, ClipboardPaste, Pencil } from 'lucide-react';
import { AuthImage } from '../../components/AuthImage';
import { formatSceneNumber } from '../../utils/sceneNumber';
import { label as L } from '../../utils/artI18n';

// ── Central column tokens (Phase 13b — top-stripe treatment) ───────────
// Panels below no longer render a tinted background; they render a
// solid `--color-panel` surface with a 2px top-stripe in the column's
// accent color. `header` remains the single source of truth for both
// the top-stripe and the header text. `bg` / `border` are kept for
// backward compatibility with any external consumer that still reads
// them, but PropCol below no longer references them.
export const ART_COL_TOKENS = {
  action_props: {
    label: 'ACTION PROPS',
    bg:     'rgba(245, 158, 11, 0.08)',
    border: 'rgba(245, 158, 11, 0.25)',
    header: 'var(--color-action-props)',
  },
  scene_props: {
    label: 'SCENE PROPS',
    bg:     'rgba(99, 102, 241, 0.08)',
    border: 'rgba(99, 102, 241, 0.25)',
    header: 'var(--color-scene-props)',
  },
  set_dressing: {
    label: 'SET DRESSING',
    bg:     'rgba(34, 197, 94, 0.08)',
    border: 'rgba(34, 197, 94, 0.25)',
    header: 'var(--color-set-dressing)',
  },
  picture_vehicle: {
    label: 'PICTURE VEHICLE',
    bg:     'rgba(147, 197, 253, 0.06)',
    border: 'rgba(147, 197, 253, 0.20)',
    header: 'var(--color-picture-vehicle)',
  },
};
export const PANEL_DIVIDER = 'var(--color-border)';
export const ADDITIONAL_NOTES_ACCENT = 'var(--color-accent)';

// ── Phase 12a — AI-confidence heatmap palette (semantic inline) ────────
// Categorical safe/warn/danger signal for the AI-suggested chip bottom
// stripe. Deliberately NOT in `artTheme.css`: this is a semantic-signal
// axis (traffic-light on AI confidence), not part of the app's chrome
// palette. Same discipline as `TypeBadge` (INT/EXT/other) and `TodBadge`
// (DAY/NIGHT/other) — categorical mini-palettes stay adjacent to their
// consumers, not in the theme token file. Adding these to the theme
// would violate Contract B (backend↔art_theme name parity) without a
// corresponding backend need.
export const CONFIDENCE_HEATMAP_TOKENS = {
  high: 'rgba(74,222,128,.85)',   // green  — confidence > 0.8
  mid:  'rgba(245,158,11,.85)',   // amber  — 0.6 ≤ confidence ≤ 0.8
  low:  'rgba(239,68,68,.85)',    // red    — confidence < 0.6
};

// ── Phase 6: Asset status pill tokens ──────────────────────────────────
// Central so the Assets tab, ArtByScene chip links, and any future
// dashboard widget all pull the same color legend.
export const ASSET_STATUS_TOKENS = {
  needed:    { label: 'Needed',    bg: 'rgba(245,158,11,.18)',  fg: '#F59E0B' },
  sourcing:  { label: 'Sourcing',  bg: 'rgba(59,130,246,.18)',  fg: '#60A5FA' },
  acquired:  { label: 'Acquired',  bg: 'rgba(34,197,94,.18)',   fg: '#4ADE80' },
  ready:     { label: 'Ready',     bg: 'rgba(16,185,129,.24)',  fg: '#34D399' },
  delivered: { label: 'Delivered', bg: 'rgba(20,184,166,.18)',  fg: '#2DD4BF' },
  returned:  { label: 'Returned',  bg: 'rgba(148,163,184,.18)', fg: '#94A3B8' },
};
export const ASSET_STATUS_ORDER = ['needed', 'sourcing', 'acquired', 'ready', 'delivered', 'returned'];

export const ASSET_CATEGORY_TOKENS = {
  action_prop:     { label: 'Action Prop',     fg: ART_COL_TOKENS.action_props.header },
  scene_prop:      { label: 'Scene Prop',      fg: ART_COL_TOKENS.scene_props.header },
  set_dressing:    { label: 'Set Dressing',    fg: ART_COL_TOKENS.set_dressing.header },
  picture_vehicle: { label: 'Picture Vehicle', fg: ART_COL_TOKENS.picture_vehicle.header },
  costume_prop:    { label: 'Costume Prop',    fg: '#F0ABFC' },
};
export const ASSET_CATEGORIES = Object.keys(ASSET_CATEGORY_TOKENS);

// ── Phase 9: Special Requirement tokens ────────────────────────────────
// 8 categories (locked by PRD v2 §7), 2 priorities, 4 statuses.
// Icons kept as short ASCII/emoji-free symbols so they render in the
// existing tinted-pill style (no font dependency).
export const REQ_CATEGORY_TOKENS = {
  fire:         { label: 'Fire',         icon: 'FIRE', fg: '#F87171', bg: 'rgba(248,113,113,.14)' },
  water_wet:    { label: 'Water / Wet',  icon: 'H2O',  fg: '#60A5FA', bg: 'rgba(96,165,250,.14)' },
  weather_fx:   { label: 'Weather FX',   icon: 'WX',   fg: '#93C5FD', bg: 'rgba(147,197,253,.14)' },
  rigging:      { label: 'Rigging',      icon: 'RIG',  fg: '#FBBF24', bg: 'rgba(251,191,36,.14)' },
  practical_fx: { label: 'Practical FX', icon: 'PFX',  fg: '#F0ABFC', bg: 'rgba(240,171,252,.14)' },
  structural:   { label: 'Structural',   icon: 'STR',  fg: '#A78BFA', bg: 'rgba(167,139,250,.14)' },
  access:       { label: 'Access',       icon: 'ACC',  fg: '#4ADE80', bg: 'rgba(74,222,128,.14)' },
  other:        { label: 'Other',        icon: 'ETC',  fg: '#94A3B8', bg: 'rgba(148,163,184,.14)' },
};
export const REQ_CATEGORIES = Object.keys(REQ_CATEGORY_TOKENS);

export const REQ_PRIORITY_TOKENS = {
  critical: { label: 'CRITICAL', bar: '#DC2626', fg: '#F87171', bg: 'rgba(220,38,38,.16)' },
  standard: { label: 'STANDARD', bar: '#F59E0B', fg: '#F59E0B', bg: 'rgba(245,158,11,.14)' },
};
export const REQ_PRIORITY_ORDER = ['critical', 'standard'];

export const REQ_STATUS_TOKENS = {
  needed:  { label: 'Needed',  fg: '#F59E0B', bg: 'rgba(245,158,11,.16)' },
  prepped: { label: 'Prepped', fg: '#60A5FA', bg: 'rgba(96,165,250,.16)' },
  ready:   { label: 'Ready',   fg: '#4ADE80', bg: 'rgba(74,222,128,.16)' },
  cleared: { label: 'Cleared', fg: '#94A3B8', bg: 'rgba(148,163,184,.16)' },
};
export const REQ_STATUS_ORDER = ['needed', 'prepped', 'ready', 'cleared'];

export function ReqPriorityPill({ priority, className = '' }) {
  const tok = REQ_PRIORITY_TOKENS[priority] || REQ_PRIORITY_TOKENS.standard;
  return (
    <span
      className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${className}`}
      style={{ background: tok.bg, color: tok.fg }}
      data-testid={`req-priority-pill-${priority}`}
    >
      {tok.label}
    </span>
  );
}

export function ReqCategoryPill({ category, className = '' }) {
  const tok = REQ_CATEGORY_TOKENS[category] || REQ_CATEGORY_TOKENS.other;
  return (
    <span
      className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${className}`}
      style={{ background: tok.bg, color: tok.fg }}
      data-testid={`req-cat-pill-${category}`}
    >
      {tok.label}
    </span>
  );
}

export function ReqStatusPill({ status, className = '' }) {
  const tok = REQ_STATUS_TOKENS[status] || REQ_STATUS_TOKENS.needed;
  return (
    <span
      className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${className}`}
      style={{ background: tok.bg, color: tok.fg }}
      data-testid={`req-status-pill-${status}`}
    >
      {tok.label}
    </span>
  );
}

export function StatusPill({ status, className = '' }) {
  const tok = ASSET_STATUS_TOKENS[status] || ASSET_STATUS_TOKENS.needed;
  return (
    <span
      className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${className}`}
      style={{ background: tok.bg, color: tok.fg }}
      data-testid={`asset-status-pill-${status}`}
    >
      {tok.label}
    </span>
  );
}

// ── Type / Time-of-day badges ──────────────────────────────────────────
export function TypeBadge({ type }) {
  const t = (type || 'INT').toUpperCase();
  const color = t === 'EXT' ? '#f59e0b' : t.startsWith('INT') ? '#3b82f6' : '#8b5cf6';
  return (
    <span data-testid="scene-type-badge" className="text-[10px] font-bold px-1.5 py-0.5 rounded"
          style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}>
      {L(`type.${t}`) || t}
    </span>
  );
}
export function TodBadge({ tod }) {
  const t = (tod || 'DAY').toUpperCase();
  const color = t === 'NIGHT' ? '#6366f1' : t === 'DAY' ? '#eab308' : '#94a3b8';
  return (
    <span data-testid="scene-tod-badge" className="text-[10px] font-bold px-1.5 py-0.5 rounded"
          style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}>
      {L(`tod.${t}`) || t}
    </span>
  );
}

// ── Prop chip (photo + description; edit/delete/attach when canWrite) ──
// Phase 6.5 (2026-02) — extended to render 4 states:
//   • freetext (legacy)     — no badges, `onConvert` menu when canWrite
//   • asset-backed          — 🔗 badge (Link2 icon)
//   • inherited (base kit)  — 📍 badge (PinIcon)
//   • both                  — BOTH badges stacked (unusual but possible;
//                              a chip that was promoted to an asset AND
//                              whose asset was later added to base kit)
// The renderer stays single-purpose — mutation logic (edit/delete/
// convert) is wired in via callbacks so this file has no state coupling.
export function Chip({
  item, onPhotoClick, canWrite, onEdit, onDelete, onAttachPhoto,
  onConvertToAsset, onToggleActionProp, onEditFull, alwaysAllowEditFull,
}) {
  const photos = item.image_paths || [];
  const hasPhoto = photos.length > 0;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.description || '');
  const [menuOpen, setMenuOpen] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const isAssetBacked = !!item.asset_id;
  const isInherited = !!item.__inherited_from_base_kit;
  // Phase 12a.1 Item 1-C — toggle is only meaningful for PROP-category
  // chips (Action Props sit within the PROP taxonomy). Set dressing and
  // picture vehicles are separate columns entirely.
  const isPropCategory = (item.category || '').toUpperCase() === 'PROP';
  const canToggleActionProp = canWrite && !isInherited && isPropCategory && !!onToggleActionProp;
  const isActionProp = !!item.is_action_prop;

  // Phase 12a — confidence heatmap: 2px bottom border colored by
  // ai_confidence when the chip was AI-suggested. Absent on freetext
  // chips (no visual change to legacy data).
  const isAiSuggested = item.origin === 'ai_suggest';
  const conf = (typeof item.ai_confidence === 'number') ? item.ai_confidence : null;
  let confBorder = null;
  let confTooltip = null;
  if (isAiSuggested) {
    const c = conf ?? 0;
    if (c > 0.8)       confBorder = CONFIDENCE_HEATMAP_TOKENS.high;
    else if (c >= 0.6) confBorder = CONFIDENCE_HEATMAP_TOKENS.mid;
    else               confBorder = CONFIDENCE_HEATMAP_TOKENS.low;
    confTooltip = `AI-suggested · confidence ${(c).toFixed(2)}`;
  }

  const commit = async () => {
    const v = draft.trim();
    setEditing(false);
    if (v && v !== (item.description || '')) await onEdit?.(item, v);
  };

  return (
    <div className="inline-flex items-center gap-1.5 rounded-full pl-1 pr-2 py-1 group relative"
         style={{
           background: 'var(--color-panel)',
           border: '1px solid var(--color-border)',
           ...(confBorder ? { borderBottom: `2px solid ${confBorder}` } : {}),
         }}
         title={confTooltip || undefined}
         data-testid="art-prop-chip"
         data-linked={isAssetBacked ? '1' : '0'}
         data-inherited={isInherited ? '1' : '0'}
         data-ai-suggested={isAiSuggested ? '1' : '0'}
         data-ai-confidence={conf !== null ? String(conf) : undefined}>
      {hasPhoto ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPhotoClick?.(item, 0); }}
          className="w-5 h-5 rounded-full overflow-hidden shrink-0"
          style={{ border: '1px solid var(--color-border)' }}
          data-testid="art-prop-chip-thumb"
        >
          <AuthImage path={photos[0]} className="w-full h-full object-cover" alt="" />
        </button>
      ) : canWrite && !isInherited ? (
        <button type="button"
          onClick={(e) => { e.stopPropagation(); onAttachPhoto?.(item); }}
          className="w-5 h-5 rounded-full flex items-center justify-center border border-dashed shrink-0 transition-colors"
          style={{
            borderColor: 'var(--color-border-strong)',
            color: 'var(--color-text-dim)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-accent)'; e.currentTarget.style.color = 'var(--color-accent)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; e.currentTarget.style.color = 'var(--color-text-dim)'; }}
          title="Attach photo"
          data-testid="art-prop-chip-attach">
          <Camera className="w-3 h-3" />
        </button>
      ) : null}
      {isAssetBacked && (
        <Link2 className="w-3 h-3" style={{ color: 'var(--color-accent)' }} data-testid="art-prop-chip-linked-badge" title="Linked to art asset" />
      )}
      {isInherited && (
        <PinIcon className="w-3 h-3" style={{ color: 'var(--color-set-dressing)' }} data-testid="art-prop-chip-inherited-badge" title="Inherited from location Base Kit" />
      )}
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setEditing(false); setDraft(item.description || ''); }
          }}
          className="text-[11px] outline-none border-b w-32"
          style={{
            background: 'var(--color-bg)',
            color: 'var(--color-accent)',
            borderBottomColor: 'var(--color-accent)',
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="art-prop-chip-edit-input"
        />
      ) : (
        <span
          className={`text-[11px] ${canWrite && !isInherited ? 'cursor-text' : ''}`}
          style={{ color: isInherited ? '#c8e6c8' : 'var(--color-text)' }}
          onClick={(e) => {
            if (!canWrite || isInherited) return;
            e.stopPropagation();
            setEditing(true);
            setDraft(item.description || '');
          }}
        >
          {item.description || '—'}
        </span>
      )}
      {(canWrite || alwaysAllowEditFull) && !editing && onEditFull && (
        <button type="button"
          onClick={(e) => { e.stopPropagation(); onEditFull(item); }}
          className="transition-opacity"
          style={{ color: 'var(--color-text-dim)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-accent)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-dim)'; }}
          title="Edit item"
          data-testid="art-prop-chip-edit-pencil">
          <Pencil className="w-3 h-3" />
        </button>
      )}
      {canWrite && !editing && !isInherited && (onConvertToAsset || canToggleActionProp || onEditFull) && (
        <div className="relative">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ color: 'var(--color-text-dim)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-accent)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-dim)'; }}
            title="More actions"
            data-testid="art-prop-chip-menu">
            <MoreHorizontal className="w-3 h-3" />
          </button>
          {menuOpen && (
            <div className="absolute top-full right-0 mt-1 z-20 rounded shadow-lg min-w-[180px]"
                 style={{ background: 'var(--color-panel)', border: '1px solid var(--color-border)' }}
                 onClick={(e) => e.stopPropagation()}>
              {onEditFull && (
                <button type="button"
                  onClick={() => { setMenuOpen(false); onEditFull(item); }}
                  className="w-full text-left text-[11px] px-2 py-1.5 flex items-center gap-1"
                  style={{ color: 'var(--color-text)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-panel-alt)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  data-testid="art-prop-chip-edit-full">
                  <Pencil className="w-3 h-3" /> Edit…
                </button>
              )}
              {canToggleActionProp && (
                <button type="button"
                  onClick={() => { setMenuOpen(false); onToggleActionProp(item); }}
                  className="w-full text-left text-[11px] px-2 py-1.5 flex items-center gap-1"
                  style={{ color: isActionProp ? 'var(--color-scene-props)' : 'var(--color-action-props)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-panel-alt)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  data-testid={isActionProp ? 'art-prop-chip-demote' : 'art-prop-chip-promote'}>
                  {isActionProp
                    ? <><ArrowDown className="w-3 h-3" /> Demote to Scene Prop</>
                    : <><ArrowUp   className="w-3 h-3" /> Promote to Action Prop</>}
                </button>
              )}
              {!isAssetBacked && onConvertToAsset && (
                <button type="button"
                  onClick={() => { setMenuOpen(false); onConvertToAsset(item); }}
                  className="w-full text-left text-[11px] px-2 py-1.5"
                  style={{ color: 'var(--color-accent)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-panel-alt)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  data-testid="art-prop-chip-convert">
                  <Link2 className="w-3 h-3 inline mr-1" /> Convert to asset
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {canWrite && !editing && (
        <button type="button"
          onClick={(e) => { e.stopPropagation(); onDelete?.(item); }}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: 'var(--color-text-dim)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-priority-critical)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-dim)'; }}
          title={isInherited ? 'Exclude from this scene' : 'Delete'}
          data-testid="art-prop-chip-delete">
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ── Tinted prop-column panel ───────────────────────────────────────────
// Commit C (Phase 12a.1 Item 8) — the chip-grid wrapper caps at 200px
// with an independent vertical scroll so a 15+ chip panel never blows
// out the drawer height.
// Commit D (Phase 12a.1 Item 11) — a compact "Bulk add" button sits next
// to "+ Add" on Action / Scene / Set-Dressing panels (NOT Picture
// Vehicles), opening a textarea modal that the parent owns.
const BULK_ADD_COLS = new Set(['action_props', 'scene_props', 'set_dressing']);

export function PropCol({ title, items, onPhotoClick, drawerMode, canWrite,
                          onEdit, onDelete, onAttachPhoto, onAdd, colKey,
                          onConvertToAsset, onSuggest, onToggleActionProp,
                          onBulkAdd, onEditFull, alwaysAllowEditFull }) {
  const shown = drawerMode ? items : items.slice(0, 2);
  const overflow = drawerMode ? 0 : Math.max(0, items.length - 2);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const addRef = useRef(null);
  useEffect(() => { if (adding) addRef.current?.focus(); }, [adding]);
  const commitAdd = async () => {
    const v = draft.trim();
    setAdding(false); setDraft('');
    if (v) await onAdd?.(colKey, v);
  };
  const isActionCol = colKey === 'action_props';
  const isVehicleCol = colKey === 'picture_vehicle';
  const canBulkAdd = drawerMode && canWrite && !!onBulkAdd && BULK_ADD_COLS.has(colKey);
  const tok = ART_COL_TOKENS[colKey] || ART_COL_TOKENS.scene_props;
  // Item 5-b: Action Props gets a 3px red urgency border-top instead of
  // the standard 2px categorical top-stripe. Reuses existing token —
  // no new theme addition needed. Signals "handled separately during
  // shooting, notice these first."
  const borderTopValue = isActionCol
    ? '3px solid var(--color-priority-critical)'
    : `2px solid ${tok.header}`;
  return (
    <div
      className="min-w-0 rounded p-2"
      style={{
        background: 'var(--color-panel)',
        border: '1px solid var(--color-border)',
        borderTop: borderTopValue,
      }}
      data-testid={`art-props-col-${colKey || title.toLowerCase().replace(/ /g,'_')}`}
    >
      <div className="text-[9px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1"
           style={{ color: tok.header }}>
        {/* Item 9: 🚗 emoji on Picture Vehicles header only (blue token
            renders correctly per computed-style check, this is a
            visual affinity boost). Item 4: append count when > 0. */}
        <span>
          {isVehicleCol && '🚗 '}
          {title}
          {items && items.length > 0 ? ` (${items.length})` : ''}
        </span>
        {drawerMode && canWrite && !adding && (
          <button type="button"
            onClick={(e) => { e.stopPropagation(); setAdding(true); }}
            className="ml-auto hover:opacity-80"
            style={{ color: tok.header }}
            title="Add chip"
            data-testid={`art-props-col-${colKey}-add`}>
            <Plus className="w-3 h-3" />
          </button>
        )}
        {canBulkAdd && !adding && (
          <button type="button"
            onClick={(e) => { e.stopPropagation(); onBulkAdd(colKey); }}
            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border hover:opacity-80"
            style={{
              borderColor: 'var(--color-border-strong)',
              color: tok.header,
              background: 'var(--color-panel-alt)',
            }}
            title="Bulk add — paste a comma or newline list"
            data-testid={`art-props-col-${colKey}-bulk-add`}>
            <ClipboardPaste className="w-3 h-3 inline -mt-0.5 mr-0.5" />
            Bulk
          </button>
        )}
        {drawerMode && canWrite && onSuggest && (
          <button type="button"
            onClick={(e) => { e.stopPropagation(); onSuggest(e.currentTarget); }}
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${adding ? 'ml-1' : 'ml-1'}`}
            style={{
              background: 'var(--color-accent-soft)',
              borderColor: 'var(--color-accent-border)',
              color: 'var(--color-accent)',
            }}
            title="Reanalyse — re-run AI suggestions for this column"
            data-testid={`art-props-col-${colKey}-suggest`}>
            🔄 Reanalyse
          </button>
        )}
      </div>
      <div
        className="flex flex-wrap gap-1 prop-col-scroll"
        style={drawerMode ? { maxHeight: '200px', overflowY: 'auto' } : undefined}
        data-testid={`art-props-col-${colKey}-scrollarea`}
      >
        {shown.length === 0 && !adding && (
          <span className="text-[10px]" style={{ color: 'var(--color-text-dim)' }}>No {title}</span>
        )}
        {shown.map((it) => (
          <Chip key={it.id}
            item={it}
            onPhotoClick={onPhotoClick}
            canWrite={canWrite && drawerMode}
            onEdit={onEdit}
            onDelete={onDelete}
            onAttachPhoto={onAttachPhoto}
            onConvertToAsset={onConvertToAsset}
            onToggleActionProp={onToggleActionProp}
            onEditFull={onEditFull}
            alwaysAllowEditFull={alwaysAllowEditFull} />
        ))}
        {adding && (
          <input
            ref={addRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitAdd}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitAdd();
              if (e.key === 'Escape') { setAdding(false); setDraft(''); }
            }}
            placeholder="+ new"
            className="text-[11px] outline-none border rounded px-1.5 py-0.5 w-28"
            style={{
              background: 'var(--color-bg)',
              color: 'var(--color-accent)',
              borderColor: 'var(--color-accent)',
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`art-props-col-${colKey}-add-input`}
          />
        )}
        {overflow > 0 && (
          <span className="text-[10px] self-center font-semibold" style={{ color: tok.header }}>
            +{overflow} more
          </span>
        )}
      </div>
    </div>
  );
}

// ── Bulk-add modal (Commit D · Phase 12a.1 Item 11) ────────────────────
// A dumb controlled component: parent owns visibility and the write
// path. Splits on newline OR comma, trims each entry, drops empties,
// and dedupes case-insensitively (whitespace-normalized). Reports
// inline feedback while the sequential single-add loop runs.
//
// PRD compliance: DOES NOT invent a new batch endpoint. The parent
// component is responsible for looping the existing
// `POST /projects/{pid}/breakdown-items` sequentially.
//
// 2026-02 copy fix (Commit D result string):
//   Result renders `Added N · Skipped M duplicates · Skipped K empties`
//   (all three segments always present, including zeros). If any
//   items failed during the sequential single-add loop, a 4th segment
//   `· Failed L` is appended and the modal stays open (existing
//   auto-close-only-on-fail-0 semantics preserved).
export function BulkAddModal({
  open, title, colKey, onClose, onSubmit,
  existingNames = [],  // optional: chip descriptions already in the
                        // panel. When provided, entries that dedupe
                        // against them count toward `skippedDuplicates`.
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const textareaRef = useRef(null);
  useEffect(() => {
    if (open) {
      setText(''); setBusy(false); setFeedback(null);
      setTimeout(() => textareaRef.current?.focus(), 30);
    }
  }, [open]);
  if (!open) return null;

  const parseEntries = (raw) => {
    // Returns { entries, skippedEmpties, skippedDuplicates } so the
    // result string can honor "always render all three counts, even
    // when zero". `existingNames` is pre-seeded into the `seen` set
    // so entries that dedupe against pre-existing chips count as
    // duplicates too (not silently ignored).
    const empty = { entries: [], skippedEmpties: 0, skippedDuplicates: 0 };
    if (!raw) return empty;
    const seen = new Set();
    for (const name of (existingNames || [])) {
      const k = String(name || '').toLowerCase().trim().replace(/\s+/g, ' ');
      if (k) seen.add(k);
    }
    const pieces = String(raw).split(/[\n,]+/);
    const out = [];
    let skippedEmpties = 0;
    let skippedDuplicates = 0;
    for (const piece of pieces) {
      const trimmed = piece.trim();
      if (!trimmed) { skippedEmpties += 1; continue; }
      const key = trimmed.toLowerCase().replace(/\s+/g, ' ');
      if (seen.has(key)) { skippedDuplicates += 1; continue; }
      seen.add(key);
      out.push(trimmed);
    }
    return { entries: out, skippedEmpties, skippedDuplicates };
  };

  const { entries, skippedEmpties, skippedDuplicates } = parseEntries(text);

  const submit = async () => {
    if (!entries.length || busy) return;
    setBusy(true);
    setFeedback({ kind: 'progress', ok: 0, fail: 0, total: entries.length });
    let ok = 0, fail = 0;
    for (let i = 0; i < entries.length; i++) {
      const desc = entries[i];
      try {
        // Parent handles the actual axios POST (loop of the existing
        // single-add endpoint) — we simply await the promise it hands
        // back so per-item errors don't nuke the whole run.
        await onSubmit(colKey, desc);
        ok += 1;
      } catch (e) {
        console.error('Bulk-add failed for entry:', desc, e);
        fail += 1;
      }
      setFeedback({ kind: 'progress', ok, fail, total: entries.length });
    }
    setBusy(false);
    // Capture the parse-time counts alongside the submit counts so the
    // final result string doesn't depend on `text` still being present
    // (it isn't, if the user clears it) or on parseEntries being run
    // again post-submit.
    setFeedback({
      kind: 'done',
      ok, fail,
      skippedDuplicates,
      skippedEmpties,
    });
    // Auto-close only when 100% of entries went through cleanly.
    if (fail === 0) {
      setTimeout(() => onClose?.(), 700);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
         data-testid="art-bulk-add-modal">
      <div className="w-full max-w-md rounded-lg"
           style={{ background: 'var(--color-panel)', border: '1px solid var(--color-border-strong)' }}>
        <div className="flex items-center justify-between px-4 py-3 border-b"
             style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex flex-col">
            <span className="text-[9px] font-bold uppercase tracking-wider"
                  style={{ color: 'var(--color-text-dim)' }}>Bulk add</span>
            <span className="text-sm font-bold" style={{ color: 'var(--color-text)' }}>{title}</span>
          </div>
          <button type="button" onClick={onClose}
                  style={{ color: 'var(--color-text-dim)' }}
                  className="hover:opacity-80"
                  data-testid="art-bulk-add-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 py-3">
          <label className="text-[10px] font-bold uppercase tracking-wider mb-2 block"
                 style={{ color: 'var(--color-text-dim)' }}>
            Paste one per line, or separate with commas
          </label>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            disabled={busy}
            placeholder={'sword\nrevolver\nleather satchel'}
            className="w-full text-[12px] rounded px-2 py-1.5 outline-none border"
            style={{
              background: 'var(--color-bg)',
              color: 'var(--color-text)',
              borderColor: 'var(--color-border-strong)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
            }}
            data-testid="art-bulk-add-textarea"
          />
          <div className="mt-2 flex items-center gap-2 text-[10px]"
               style={{ color: 'var(--color-text-dim)' }}>
            {/* Pre-submit hint uses the same 3-count vocabulary as the
                final result, so the user sees exactly what will happen
                before clicking Add. The word "empties" is intentional
                plural per spec — matches result string. */}
            <span data-testid="art-bulk-add-parsed-count">
              {entries.length} to add · {skippedDuplicates} duplicates · {skippedEmpties} empties
            </span>
            {feedback && feedback.kind === 'progress' && (
              <span style={{ color: 'var(--color-accent)' }}
                    data-testid="art-bulk-add-progress">
                · adding {feedback.ok + feedback.fail} / {feedback.total}
                {feedback.fail > 0 && ` (${feedback.fail} failed)`}
              </span>
            )}
            {feedback && feedback.kind === 'done' && (
              <span style={{ color: feedback.fail > 0 ? 'var(--color-priority-critical)' : 'var(--color-set-dressing)' }}
                    data-testid="art-bulk-add-result">
                {`· Added ${feedback.ok} · Skipped ${feedback.skippedDuplicates} duplicates · Skipped ${feedback.skippedEmpties} empties`}
                {feedback.fail > 0 && ` · Failed ${feedback.fail}`}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t"
             style={{ borderColor: 'var(--color-border)' }}>
          <button type="button" onClick={onClose} disabled={busy}
                  className="text-[11px] px-3 py-1.5 rounded border"
                  style={{
                    borderColor: 'var(--color-border-strong)',
                    color: 'var(--color-text-sec)',
                    background: 'transparent',
                  }}
                  data-testid="art-bulk-add-cancel">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!entries.length || busy}
                  className="text-[11px] px-3 py-1.5 rounded border font-bold"
                  style={{
                    borderColor: 'var(--color-accent)',
                    color: entries.length && !busy ? 'var(--color-accent)' : 'var(--color-text-dim)',
                    background: 'var(--color-accent-soft)',
                    opacity: entries.length && !busy ? 1 : 0.5,
                    cursor: entries.length && !busy ? 'pointer' : 'not-allowed',
                  }}
                  data-testid="art-bulk-add-submit">
            {busy ? 'Adding…' : `Add ${entries.length || ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Lightbox (photo pager with keyboard nav + filmstrip) ───────────────
export function Lightbox({ items, index, onClose, onNav, captionPrefix }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape')     { e.stopPropagation(); onClose(); }
      if (e.key === 'ArrowLeft')  { e.stopPropagation(); onNav(-1); }
      if (e.key === 'ArrowRight') { e.stopPropagation(); onNav(1); }
    };
    // Capture-phase so we run before any Drawer/window listener.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, onNav]);
  if (index === -1) return null;
  const cur = items[index];
  if (!cur) return null;
  const caption = captionPrefix
    ? `${captionPrefix} · photo ${index + 1} of ${items.length}`
    : `Scene ${formatSceneNumber(cur.sceneNumber)} · ${cur.itemName} · photo ${index + 1} of ${items.length}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="art-lightbox">
      <div className="flex items-center gap-3 px-4 py-3 text-white">
        <span className="font-semibold">{caption}</span>
        <button onClick={onClose} className="ml-auto text-white/70 hover:text-white" data-testid="art-lightbox-close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center relative">
        <button onClick={() => onNav(-1)} className="absolute left-4 text-white/70 hover:text-white" data-testid="art-lightbox-prev">
          <ChevronLeft className="w-8 h-8" />
        </button>
        <AuthImage path={cur.path} className="max-w-[85%] max-h-[85%] object-contain" alt="" />
        <button onClick={() => onNav(1)} className="absolute right-4 text-white/70 hover:text-white" data-testid="art-lightbox-next">
          <ChevronRight className="w-8 h-8" />
        </button>
      </div>
      <div className="flex gap-1 px-4 py-3 overflow-x-auto">
        {items.map((it, i) => (
          <button key={i} onClick={() => onNav(i - index)}
            className={`w-14 h-14 rounded overflow-hidden border-2 shrink-0 ${i === index ? '' : 'opacity-60 hover:opacity-100'}`}
            style={{ borderColor: i === index ? 'var(--color-accent)' : 'transparent' }}
            data-testid={`art-lightbox-strip-${i}`}>
            <AuthImage path={it.path} className="w-full h-full object-cover" alt="" />
          </button>
        ))}
      </div>
    </div>
  );
}
